import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { STORE, SITE } from "@/config/sections";
import { ContactForm } from "@/components/ContactForm";

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`policies-${id}`} className="mt-12 scroll-mt-28">
    <h2 className="font-heading text-2xl font-extrabold tracking-tight">{title}</h2>
    <div className="mt-4 space-y-3 text-stone-600 leading-relaxed">{children}</div>
  </section>
);

const FACTS = [
  ["Ships to", "United States"],
  ["Shipping cost", "Free over $50, otherwise $5.99"],
  ["Delivery", "4–7 business days total"],
  ["Returns", "Free, within 30 days"],
];

export default function PoliciesPage() {
  useSeo({ title: "Shipping, Returns & Contact | PetTok Shop", description: "US shipping $5.99, free over $50, delivered in 4-7 business days. Free 30-day returns with a prepaid label. Contact PetTok Shop.", path: "/policies" });
  return (
    <main data-testid="policies-page" className="pt-32 pb-10 max-w-3xl mx-auto px-6">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#E06D44]">The small print, kept small</p>
      <h1 className="font-heading mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight">Shipping, returns & contact</h1>

      <dl data-testid="policies-summary" className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {FACTS.map(([k, v]) => (
          <div key={k} className="rounded-2xl bg-white border border-[#F0EBE1] p-4">
            <dt className="text-[11px] uppercase tracking-[0.15em] text-stone-500">{k}</dt>
            <dd className="mt-1 font-heading font-bold text-sm">{v}</dd>
          </div>
        ))}
      </dl>

      <Section id="shipping" title="Shipping">
        <p>We ship to addresses in the United States. We don't ship internationally yet.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Free shipping</strong> on orders of $50 or more (after discounts).</li>
          <li>Orders under $50 ship for a flat <strong>$5.99</strong>.</li>
          <li><strong>Processing:</strong> orders are packed and handed to the carrier within <strong>1–2 business days</strong> (Monday–Friday, excluding US holidays).</li>
          <li><strong>Transit:</strong> delivery takes <strong>3–5 business days</strong> after shipping, so most orders arrive within <strong>4–7 business days</strong> of ordering.</li>
          <li>You'll get an email with your tracking number as soon as your order ships.</li>
        </ul>
        <p>You can check on any order with <Link to="/track" className="underline text-[#E06D44]">Track an order</Link>, using your order number and email. If a package is late, lost or arrives damaged, contact us and we'll send a replacement or a full refund.</p>
      </Section>

      <Section id="returns" title="Returns & refunds">
        <p><strong>Happy pets or your money back.</strong> If you or your pet aren't happy with something, contact us within <strong>30 days</strong> of delivery and we'll make it right with a refund.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Returns are free.</strong> We send you a prepaid return label, so you never pay return shipping. There's no restocking fee.</li>
          <li>To start a return, contact us with your order number and what went wrong. Returns are by mail only.</li>
          <li>We may ask you to send unused items back. Opened treats and chewed toys don't need to be returned.</li>
          <li>Damaged, faulty or wrong items are refunded or replaced in full; a photo helps us sort it quickly.</li>
          <li>Refunds go back to your original payment method, usually within 5–10 business days after we receive the return or approve it.</li>
          <li>Exchanges: return the original for a refund and place a new order, so you get the new item faster.</li>
        </ul>
      </Section>

      <Section id="contact" title="Contact us">
        <p>{SITE.name} · {STORE.address}</p>
        <p>Email: <a data-testid="policies-contact-email" href={`mailto:${STORE.email}`} className="underline text-[#E06D44]">{STORE.email}</a></p>
        <p>Or send us a message here:</p>
        <ContactForm />
      </Section>
    </main>
  );
}
